/**
 * Platform API service for dynamic service discovery and health checking
 */

import axios, { AxiosResponse } from 'axios';

// ---------------------------------------------------------------------------
// Authed admin API client — mirrors services/connectorApi.ts (token lookup,
// Authorization header attachment, 401 handling). Used by SettingsManager
// and UserManager so those tabs stop sending unauthenticated raw fetch()
// calls against the require_admin()-guarded /admin/settings + /admin/users
// endpoints.
// ---------------------------------------------------------------------------

// Get API base URL dynamically based on environment
const getAdminApiBaseUrl = (): string => {
  // Check for environment variable first (production)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // For development/local - use current origin with /api/v1 path
  // This leverages the nginx proxy configuration
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/v1`;
  }

  // Fallback for SSR or edge cases
  return '/api/v1';
};

// Create axios instance with dynamic base URL
const createAdminApiInstance = () => {
  const baseURL = getAdminApiBaseUrl();

  const apiInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Add auth token to requests if available
  apiInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor for error handling
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Handle unauthorized access
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return apiInstance;
};

// Get API instance (lazy-loaded)
const getAdminApi = () => createAdminApiInstance();

export class AdminApiService {
  // Settings

  async getSettings(): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.get('/admin/settings');
    return response.data;
  }

  async updateSettings(values: any): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.put('/admin/settings', values);
    return response.data;
  }

  async testLlmConnection(payload: any): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.post('/admin/settings/test-llm', payload);
    return response.data;
  }

  async getBedrockModels(): Promise<any[]> {
    const api = getAdminApi();
    const response: AxiosResponse<any[]> = await api.get('/admin/models/bedrock');
    return response.data;
  }

  async getModelCategories(): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.get('/admin/models/categories');
    return response.data;
  }

  // Users

  async listUsers(): Promise<any[]> {
    const api = getAdminApi();
    const response: AxiosResponse<any[]> = await api.get('/admin/users');
    return response.data;
  }

  async createUser(values: any): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.post('/admin/users', values);
    return response.data;
  }

  async updateUser(userId: string, values: any): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.put(`/admin/users/${userId}`, values);
    return response.data;
  }

  async deleteUser(userId: string): Promise<any> {
    const api = getAdminApi();
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async resetPassword(userId: string): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.post(`/admin/users/${userId}/reset-password`);
    return response.data;
  }

  async regenerateApiKey(userId: string): Promise<any> {
    const api = getAdminApi();
    const response: AxiosResponse<any> = await api.post(`/admin/users/${userId}/regenerate-api-key`);
    return response.data;
  }
}

export const adminApi = new AdminApiService();

export interface ServiceStatus {
  id: string;
  name: string;
  description: string;
  url: string;
  status: 'online' | 'offline' | 'starting';
  lastChecked: Date;
  responseTime?: number;
}

export interface PlatformConfig {
  services: {
    declass: { port: number; path: string };
    legal: { port: number; path: string };
    admin: { port: number; path: string };
  };
  api: {
    port: number;
    baseUrl: string;
  };
}

class PlatformApiService {
  private config: PlatformConfig;
  private baseUrl: string;
  
  constructor() {
    // Dynamic configuration based on environment
    this.baseUrl = this.getBaseUrl();
    this.config = this.getConfig();
  }
  
  private getBaseUrl(): string {
    // In development, use localhost with API port
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:8000';
    }
    
    // In production, use the same origin (assuming reverse proxy setup)
    return window.location.origin;
  }
  
  private getConfig(): PlatformConfig {
    // Get configuration from environment or use defaults
    const isDev = process.env.NODE_ENV === 'development';
    
    return {
      services: {
        declass: {
          port: isDev ? 3000 : parseInt(process.env.REACT_APP_DECLASS_PORT || '3000'),
          path: '/app/declass'
        },
        legal: {
          port: isDev ? 3001 : parseInt(process.env.REACT_APP_LEGAL_PORT || '3001'),
          path: '/app/legal'
        },
        admin: {
          port: isDev ? 3004 : parseInt(process.env.REACT_APP_ADMIN_PORT || '3004'),
          path: '/admin'
        }
      },
      api: {
        port: isDev ? 8000 : parseInt(process.env.REACT_APP_API_PORT || '8000'),
        baseUrl: this.baseUrl
      }
    };
  }
  
  /**
   * Get service URL for a domain
   */
  public getServiceUrl(serviceId: string): string {
    const service = this.config.services[serviceId as keyof typeof this.config.services];
    if (!service) {
      throw new Error(`Unknown service: ${serviceId}`);
    }

    // In development, use localhost with specific port
    if (process.env.NODE_ENV === 'development') {
      return `http://localhost:${service.port}`;
    }

    // In production, use subdomain-based routing
    const currentUrl = new URL(window.location.href);
    const baseDomain = currentUrl.hostname.replace(/^[^.]+\./, ''); // Get base domain (e.g., cognitiveoutcomes.us)

    return `${currentUrl.protocol}//${serviceId}.${baseDomain}`;
  }
  
  /**
   * Check if a service is healthy
   */
  public async checkServiceHealth(serviceId: string): Promise<ServiceStatus> {
    const startTime = Date.now();

    try {
      const url = this.getServiceUrl(serviceId);
      // All frontend services have /health endpoint
      const healthUrl = `${url}/health`;

      const response = await fetch(healthUrl, {
        method: 'GET',
        mode: 'cors'
      } as any);
      
      const responseTime = Date.now() - startTime;
      const isHealthy = response.ok;
      
      return {
        id: serviceId,
        name: this.getServiceName(serviceId),
        description: this.getServiceDescription(serviceId),
        url: this.getServiceUrl(serviceId),
        status: isHealthy ? 'online' : 'offline',
        lastChecked: new Date(),
        responseTime
      };
      
    } catch (error) {
      return {
        id: serviceId,
        name: this.getServiceName(serviceId),
        description: this.getServiceDescription(serviceId),
        url: this.getServiceUrl(serviceId),
        status: 'offline',
        lastChecked: new Date()
      };
    }
  }
  
  /**
   * Check all services health
   */
  public async checkAllServicesHealth(): Promise<ServiceStatus[]> {
    const serviceIds = ['declass', 'legal'];
    const healthChecks = serviceIds.map(id => this.checkServiceHealth(id));

    return Promise.all(healthChecks);
  }
  
  /**
   * Get platform capabilities from API
   */
  public async getPlatformCapabilities(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/platform/capabilities`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch platform capabilities');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching platform capabilities:', error);
      return null;
    }
  }
  
  /**
   * Get domain-specific capabilities
   */
  public async getDomainCapabilities(domain: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/${domain}/capabilities`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${domain} capabilities`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${domain} capabilities:`, error);
      return null;
    }
  }
  
  private getServiceName(serviceId: string): string {
    const names: Record<string, string> = {
      declass: 'DECLASS',
      legal: 'LegalFabric',
      admin: 'Cognitive Fabric Admin'
    };
    return names[serviceId] || serviceId;
  }

  private getServiceDescription(serviceId: string): string {
    const descriptions: Record<string, string> = {
      declass: 'Defense Intelligence Classification & Analysis',
      legal: 'Legal eDiscovery & Privilege Review',
      admin: 'Cognitive Fabric Control Center'
    };
    return descriptions[serviceId] || 'Unknown service';
  }
}

export const platformApi = new PlatformApiService();